import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useIdleStore } from "../../store/useIdleStore";
import { IDLE_CONFIG } from "../../config/IdleConfig";

const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];
const AUTH_ROUTES = ["/login", "/teacher", "/teacher/login", "/forgot-password", "/register", "/logout"]; 

export default function IdleTracker() {
  const location = useLocation();
  const { markIdle, showPopup, setActivity, showIdlePopup, resetIdle } = useIdleStore();
  const idleTimeout = useRef(null);

  const isAuthenticated = () =>
    !!(localStorage.getItem("Token") || localStorage.getItem("access_token"));

  const isAuthRoute = AUTH_ROUTES.includes(location.pathname);
  const shouldTrackIdle = isAuthenticated() && !isAuthRoute;

  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimeout.current);
    idleTimeout.current = setTimeout(() => {
      localStorage.setItem("idle_start_time", Date.now().toString());
      localStorage.setItem("idle_popup_visible", "true");
      markIdle();
      showPopup();
    }, IDLE_CONFIG.IDLE_TIME_MS);
  }, [idleTimeout, markIdle, showPopup]);

  useEffect(() => {
    if (!shouldTrackIdle) {
      clearTimeout(idleTimeout.current);
      return;
    }

    const handleActivity = () => {
      if (showIdlePopup) return;
      setActivity();
      resetIdleTimer();
      localStorage.removeItem("idle_start_time");
      localStorage.setItem("last_activity", Date.now().toString());
      localStorage.setItem("idle_popup_visible", "false");
    };

    events.forEach((e) => window.addEventListener(e, handleActivity));
    resetIdleTimer();

    // Wakeup handler when user returns to a hidden tab
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && shouldTrackIdle) {
        const idleStart = localStorage.getItem("idle_start_time");
        if (idleStart) {
          const elapsed = Date.now() - parseInt(idleStart, 10);
          const totalIdleMs = IDLE_CONFIG.COUNTDOWN_SECONDS * 1000;
          if (elapsed >= totalIdleMs) {
            useIdleStore.getState().logout();
            localStorage.removeItem("idle_start_time");
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    // Cross-tab storage event listener
    const handleStorage = (e) => {
      if (e.key === "last_activity") {
        setActivity();
        resetIdleTimer();
        localStorage.removeItem("idle_start_time");
      }
      if (e.key === "idle_popup_visible") {
        if (e.newValue === "true" && !showIdlePopup) {
          markIdle();
          showPopup();
        } else if (e.newValue === "false" && showIdlePopup) {
          resetIdle();
        }
      }
      if (e.key === "idle_popup_end_time" && showIdlePopup) {
        const endTime = parseInt(e.newValue, 10);
        if (!isNaN(endTime)) {
          useIdleStore.setState({ idlePopupEndTime: endTime });
          const now = Date.now();
          const secondsLeft = Math.max(0, Math.ceil((endTime - now) / 1000));
          useIdleStore.setState({ countdown: secondsLeft });
        }
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("storage", handleStorage);
      clearTimeout(idleTimeout.current);
    };
  }, [shouldTrackIdle, showIdlePopup, setActivity, resetIdleTimer, markIdle, showPopup, resetIdle]);

  // Re-arm when popup is manually dismissed
  useEffect(() => {
    if (shouldTrackIdle && !showIdlePopup) {
      resetIdleTimer();
    }
  }, [showIdlePopup, shouldTrackIdle, resetIdleTimer]);

  return null;
}
